import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
} from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Slide-over panel built on Radix Dialog. Used for task editors and
 * mobile menus. Animates from the right by default.
 */
export const Sheet = RadixDialog.Root;
export const SheetTrigger = RadixDialog.Trigger;
export const SheetClose = RadixDialog.Close;

type Side = 'right' | 'left' | 'bottom' | 'top';

const sideClasses: Record<Side, string> = {
  right:
    'right-0 top-0 h-full w-[min(92vw,420px)] data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
  left: 'left-0 top-0 h-full w-[min(92vw,420px)] data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
  bottom:
    'bottom-0 left-0 right-0 max-h-[88vh] rounded-t-2xl data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
  top: 'top-0 left-0 right-0 max-h-[88vh] rounded-b-2xl data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top',
};

interface SheetContentProps extends ComponentPropsWithoutRef<typeof RadixDialog.Content> {
  side?: Side;
  showClose?: boolean;
}

export const SheetContent = forwardRef<ElementRef<typeof RadixDialog.Content>, SheetContentProps>(
  function SheetContent({ className, side = 'right', showClose = true, children, ...rest }, ref) {
    return (
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className={cn(
            // Translucent scrim only — no backdrop-blur (paper, not glass).
            'fixed inset-0 z-overlay bg-scrim',
            // Duration rides the data-state variants — they out-specify a bare
            // duration class (attribute selector in the variant).
            'ease-out-strong data-[state=open]:duration-modal data-[state=closed]:duration-modal',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            'motion-reduce:animate-none',
          )}
        />
        <RadixDialog.Content
          ref={ref}
          className={cn(
            'fixed z-overlay flex flex-col border-line-2 bg-canvas p-5 shadow-window outline-none',
            // Scroll tall content (forms, editors) instead of clipping it — the
            // bottom sheet is capped at 88vh and would otherwise hide the
            // Save/footer below the fold. Safe-area bottom padding clears the
            // home indicator.
            'overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1.25rem)]',
            // iOS-feel drawer curve at the modal duration (variant-scoped — see
            // the overlay note).
            'ease-drawer data-[state=open]:duration-modal data-[state=closed]:duration-modal',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'motion-reduce:animate-none',
            side === 'right' || side === 'left' ? 'border-l border-r' : 'border-t border-b',
            sideClasses[side],
            className,
          )}
          {...rest}
        >
          {children}
          {showClose ? (
            <RadixDialog.Close
              className={cn(
                'absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-md text-t3',
                'pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]',
                'transition-colors can-hover:hover:bg-rail can-hover:hover:text-t1',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
              )}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </RadixDialog.Close>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    );
  },
);

export const SheetTitle = forwardRef<
  ElementRef<typeof RadixDialog.Title>,
  ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(function SheetTitle({ className, ...rest }, ref) {
  return (
    <RadixDialog.Title
      ref={ref}
      // break-words: sheet titles often carry user content (habit names) —
      // an unbroken string must wrap, not overflow the sheet.
      // pe-8/pe-12: clearance for the absolute close × (right-3, 28px / 44px
      // coarse intrudes 20px/36px past the content padding) — long titles must
      // not run under it.
      className={cn(
        'break-words pe-8 pointer-coarse:pe-12 text-title font-medium text-t1',
        className,
      )}
      {...rest}
    />
  );
});

export const SheetDescription = forwardRef<
  ElementRef<typeof RadixDialog.Description>,
  ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(function SheetDescription({ className, ...rest }, ref) {
  return (
    <RadixDialog.Description
      ref={ref}
      className={cn('mt-1 text-body text-t2', className)}
      {...rest}
    />
  );
});

export function SheetFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('mt-auto flex items-center justify-end gap-2 pt-4', className)} {...rest} />
  );
}
